<template>
    <div class="w-full" v-if="contents">
        <div class="max-w-6xl mx-auto px-6 py-8">
            <!-- Hero Section -->
            <div class="bg-gradient-to-r from-nomad-800 to-nomad-900 rounded-lg overflow-hidden mb-8 p-6 md:p-8">
                <img src="https://cdn.nomadlyrics.com/static/artists/UJFpkk4dV0Xes1R6TSPcJzxcG7OyQkLZDc1kPuHY.png"
                    class="w-full rounded-lg mb-6 object-cover" alt="">
                <h1 class="text-4xl md:text-5xl font-bold text-white mb-4">{{ contents.title }}</h1>
                <p class="text-nomad-200 font-light text-lg leading-relaxed">Heesify is more than just music—it's a full
                    Somali audio platform. From
                    music and lyrics to podcasts, interviews, audiobooks, and documentaries, everything lives in one
                    place. Our iOS and Android apps make it easy to listen, discover, and stay connected to Somali
                    voices anywhere in the world.</p>
            </div>

            <!-- Key Features -->
            <div class="bg-nomad-800 rounded-lg p-6 mb-8">
                <h2 class="text-3xl font-bold text-white mb-6">Key Features</h2>
                <ul class="space-y-3">
                    <li class="flex items-start gap-3">
                        <span class="text-nomad-accent font-bold text-xl flex-shrink-0">•</span>
                        <span class="text-nomad-200 font-light">Massive Lyrics Library: Find lyrics for thousands of
                            songs from hundreds of Somali
                            artists.</span>
                    </li>
                    <li class="flex items-start gap-3">
                        <span class="text-nomad-accent font-bold text-xl flex-shrink-0">•</span>
                        <span class="text-nomad-200 font-light">Discover New and Classic Music: Explore trending tracks
                            and timeless songs dating
                            back to the 1950s.</span>
                    </li>
                    <li class="flex items-start gap-3">
                        <span class="text-nomad-accent font-bold text-xl flex-shrink-0">•</span>
                        <span class="text-nomad-200 font-light">Simple, User-Friendly Design: Built for an easy and
                            enjoyable listening experience.</span>
                    </li>
                    <li class="flex items-start gap-3">
                        <span class="text-nomad-accent font-bold text-xl flex-shrink-0">•</span>
                        <span class="text-nomad-200 font-light">The Leading Somali Audio Platform: Trusted by listeners
                            around the world.</span>
                    </li>
                </ul>
            </div>

            <!-- Screenshots -->
            <div class="mb-8">
                <h2 class="text-3xl font-bold text-white mb-6">Screenshots</h2>
                <Carousel v-if="slides" :items-to-show="4" :breakpoints="breakpoints" :wrapAround="true"
                    :transition="500">
                    <Slide v-for="slide in slides" :key="slide.id">
                        <div class="w-full rounded-lg overflow-hidden m-2 shadow-lg">
                            <img :src="slide.image" :alt="slide.title" :title="slide.title" class="w-full" />
                        </div>
                    </Slide>
                </Carousel>
            </div>

            <!-- Download Section -->
            <div class="bg-nomad-800 rounded-lg p-8 mb-8">
                <h2 class="text-3xl font-bold text-white mb-2">Get the Apps</h2>
                <p class="text-nomad-300 text-sm mb-4">last updated: 04/22/2024</p>
                <Link href="/pages/apps-changelog"
                    class="text-nomad-accent hover:text-nomad-accent/80 font-semibold mb-6 inline-flex items-center gap-2 transition-colors">
                Changelog <span class="icon-external-link"></span>
                </Link>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                    <a href="https://apps.apple.com/app/apple-store/id1503789749?pt=121219390&ct=NomadLyricsWebsite&mt=8"
                        class="flex items-center justify-center px-6 py-3 bg-nomad-accent hover:bg-nomad-accent/80 text-nomad-900 font-semibold rounded-lg transition-all hover:shadow-lg"
                        target="_blank">
                        <span class="icon-apple mr-2"></span>
                        Download on the App Store
                    </a>
                    <a href="https://play.google.com/store/apps/details?id=com.nomadlyrics"
                        class="flex items-center justify-center px-6 py-3 bg-nomad-accent hover:bg-nomad-accent/80 text-nomad-900 font-semibold rounded-lg transition-all hover:shadow-lg"
                        target="_blank">
                        <span class="icon-google mr-2"></span>
                        Get it on Google Play
                    </a>
                </div>
            </div>

            <!-- Why Heesify -->
            <div class="bg-nomad-800 rounded-lg p-6 mb-8">
                <h2 class="text-3xl font-bold text-white mb-6">Why Heesify</h2>
                <ul class="space-y-3">
                    <li class="flex items-start gap-3">
                        <span class="text-nomad-accent font-bold text-xl flex-shrink-0">•</span>
                        <span class="text-nomad-200 font-light">All-in-One Somali Audio: Music, lyrics, podcasts,
                            interviews, audiobooks, and
                            documentaries in one place.</span>
                    </li>
                    <li class="flex items-start gap-3">
                        <span class="text-nomad-accent font-bold text-xl flex-shrink-0">•</span>
                        <span class="text-nomad-200 font-light">Smart Discovery: Stay updated with trending content and
                            discover new voices across
                            the Somali community.</span>
                    </li>
                    <li class="flex items-start gap-3">
                        <span class="text-nomad-accent font-bold text-xl flex-shrink-0">•</span>
                        <span class="text-nomad-200 font-light">Verified & Trusted: We still care about accuracy—lyrics,
                            titles, and audio are
                            carefully reviewed.</span>
                    </li>
                    <li class="flex items-start gap-3">
                        <span class="text-nomad-accent font-bold text-xl flex-shrink-0">•</span>
                        <span class="text-nomad-200 font-light">Built for Listeners: Our apps are designed for smooth,
                            simple listening and
                            exploring.</span>
                    </li>
                </ul>
            </div>

            <!-- Impact Section -->
            <div class="bg-nomad-800 rounded-lg p-6 mb-8">
                <p class="text-nomad-200 font-light text-lg leading-relaxed">Over 1.2 million minutes of Somali music,
                    poetry, interviews, and
                    literature are streamed on Heesify every month. Help us keep the platform alive and growing. Your
                    support helps preserve and celebrate Somali cultural heritage, connect listeners around the world,
                    and uplift talented artists.</p>
            </div>

            <!-- Advertise Section -->
            <div class="bg-nomad-800 rounded-lg overflow-hidden p-6 mb-8">
                <h2 class="text-3xl font-bold text-white mb-4">Advertise with us</h2>
                <p class="text-nomad-200 font-light mb-4">Reach a passionate audience of Somali culture enthusiasts.</p>
                <img src="https://cdn.nomadlyrics.com/static/artists/Yu81iGGdXGUkcfkvRb7K357vU2sNVotxXh4NEgHx.png"
                    class="w-full rounded-lg object-cover" alt="Advertise with us">
            </div>

            <!-- Contact Section -->
            <div class="bg-nomad-800 rounded-lg p-6">
                <p class="text-nomad-200 font-light">Reach us at: <a href="mailto:hello@heesify.com"
                        class="text-nomad-accent hover:text-nomad-accent/80 font-semibold transition-colors">hello@heesify.com</a>
                </p>
            </div>
        </div>
    </div>
</template>
<script>
import { Carousel, Slide, Pagination, Navigation } from "vue3-carousel";
import { Link } from "@inertiajs/vue3";
export default {
    props: {
        contents: {
            type: Object
        }
    },
    data() {
        return {
            slides: [
                {
                    id: 1,
                    image: 'https://cdn.nomadlyrics.com/static/artists/p8Q5ileEbYi85EaauGmHCOxAhmpHiOZpYknyqhRy.png',
                    title: 'Discover New Music and Artists'
                },
                {
                    id: 2,
                    image: 'https://cdn.nomadlyrics.com/static/artists/jUxoSyd1FiiUwXG1qVONLcmux4j3dP971cPRJLvT.png',
                    title: 'Events and Concerts'
                },
                {
                    id: 3,
                    image: 'https://cdn.nomadlyrics.com/static/artists/NSJTY0UVKQzZtiq3cv8BNP0NA36R56Pbbm3kfMIp.png',
                    title: 'Your favorites artists and more'
                },
                {
                    id: 4,
                    image: 'https://cdn.nomadlyrics.com/static/artists/QYOq5A7Q2j5i5jflBYI86kJKY2ptp19IKiWRPPm1.png',
                    title: 'Podcasts'
                }
            ],
            breakpoints: {
                // 700px and up
                100: {
                    itemsToShow: 1.2,
                    snapAlign: "center",
                },
                700: {
                    itemsToShow: 2.2,
                    snapAlign: "center",
                },
                // 1024 and up
                1024: {
                    itemsToShow: 3,
                    snapAlign: "start",
                },
            },
        }
    },
    components: {
        Carousel,
        Slide,
        Link
    }
}
</script>